import type { Capabilities } from '../../api/meta';
import type { CurrentUser, DirectoryUser } from '../../app/state';
import type { Permissions } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * Who is signed in, what they may do, and what this deployment can do at all.
 *
 * THE TWO GATES ARE KEPT STRUCTURALLY SEPARATE and both live here because both are answers
 * the server gave about this session. `perm` (from `GET /api/auth/me`) says may THIS USER;
 * `caps` (from `GET /api/capabilities`) says does THIS DEPLOYMENT have the feature at all.
 * A control is drawn only when both pass — see `lib/permissions.ts` and `lib/capabilities.ts`,
 * which are the predicates, deliberately not folded into one. The reason is the air-gapped
 * build: an AI surface must be ABSENT, never present-and-erroring.
 *
 * `caps` sits in this slice rather than a tenth one because it has the same lifetime as the
 * session — read once at boot, and re-read only when the session is re-established.
 *
 * WHAT IS NOT HERE: `booted`. The bridge carries a single boolean because that is all its
 * consumers needed ("has the session question been asked yet"). The store has the whole boot
 * sequence instead — see the `boot` slice — and the boolean is derived from it.
 *
 * SIDE EFFECT, DELIBERATELY NOT HERE EITHER: publishing `perm` to
 * `sessionStorage["userPermissions"].documentation` is a suite-wide integration contract that
 * other CONIGON apps read. It lives in `store/persistence.ts` with the other storage writes,
 * so every key this app touches is described in one file.
 */
export interface AuthSlice {
  /** Null when signed out. One fact, so nothing can disagree with it. */
  currentUser: CurrentUser | null;
  perm: Permissions | null;
  /** Null means "not asked / unknown", which is not the same as "no AI". */
  caps: Capabilities | null;
  /** The locale the server resolved for this session — it may differ from the one asked for. */
  currentLocale: string;
  /**
   * Every account with its eligibility flags, from `GET /api/users/directory` — the only
   * non-admin way to turn a `User.id` into a name, which Phase 4a's node ownership needs.
   *
   * It belongs in THIS slice rather than the docs one: it is a fact about accounts with the
   * same lifetime as the session — read once per sign-in, gated on `SHOW_EDIT`, and left empty
   * for everyone else. Adopted by reference; the loader replaces the array whole.
   */
  userDirectory: readonly DirectoryUser[];
  /** "Nobody else has an account" and "we have not asked" are different owner pickers. */
  userDirectoryLoaded: boolean;

  setSession: (user: CurrentUser | null, perm: Permissions | null) => void;
  setCapabilities: (caps: Capabilities | null) => void;
  setCurrentLocale: (locale: string) => void;
  setUserDirectory: (users: readonly DirectoryUser[], loaded: boolean) => void;
}

export const createAuthSlice: SliceCreator<AuthSlice> = (set, get) => ({
  currentUser: null,
  perm: null,
  caps: null,
  currentLocale: 'en',
  userDirectory: [],
  userDirectoryLoaded: false,

  // All three refuse a write that changes nothing. The mirror re-states them on every sync,
  // and a notification costs one selector evaluation per subscribed map node — see the note
  // in `slices/ui.ts`. Identity comparison is the right test here: these are the vanilla
  // script's own objects, handed across unchanged unless the session was re-established.
  setSession: (user, perm) => {
    const s = get();
    if (s.currentUser === user && s.perm === perm) return;
    set({ currentUser: user, perm });
  },
  setCapabilities: (caps) => {
    if (get().caps === caps) return;
    set({ caps });
  },
  setCurrentLocale: (locale) => {
    const next = locale || 'en';
    if (get().currentLocale === next) return;
    set({ currentLocale: next });
  },
  // Identity again, and it is exact here: the array is replaced wholesale by its one loader
  // and never edited, so a different reference really does mean a different directory.
  setUserDirectory: (users, loaded) => {
    const s = get();
    if (s.userDirectory === users && s.userDirectoryLoaded === loaded) return;
    set({ userDirectory: users, userDirectoryLoaded: loaded });
  },
});
